import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { onboardingApi } from '../utils/api';
import { useAuth } from './AuthContext';

/**
 * Entitlements + onboarding funnel state (Online store Phase 0, slice C).
 *
 * One request after login (GET /api/onboarding) tells the app which paid
 * features this shop has and whether the Getting Started checklist applies.
 * The sidebar, the settings drawer and feature screens read from here instead
 * of each fetching. `entitlements` is null until loaded, so consumers can
 * avoid hiding things during the first render.
 *
 * Non-admin staff get a 403 from that endpoint; they see no funnel and, until
 * a lighter endpoint exists, an empty entitlement list. Nothing they use today
 * is gated.
 */
const EntitlementsContext = createContext({
  entitlements: null,
  known: false,
  has: () => true,
  lacks: () => false,
  onboarding: null,
  loaded: false,
  reload: () => {},
});

export const EntitlementsProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [entitlements, setEntitlements] = useState(null);
  // True only when the server actually answered with this shop's list. Non-admin
  // staff (403) and failed requests leave it false, so nothing is upsold to them.
  const [known, setKnown] = useState(false);
  const [onboarding, setOnboarding] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const r = await onboardingApi.get();
      if (r?.success) {
        setEntitlements(Array.isArray(r.data.entitlements) ? r.data.entitlements : []);
        setKnown(Array.isArray(r.data.entitlements));
        setOnboarding({ applicable: !!r.data.applicable, progress: r.data.progress, steps: r.data.steps });
      } else {
        setEntitlements([]);
      }
    } catch (err) {
      setEntitlements([]);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setEntitlements(null);
      setKnown(false);
      setOnboarding(null);
      setLoaded(false);
      return;
    }
    reload();
  }, [isAuthenticated, reload]);

  const value = useMemo(() => ({
    entitlements,
    // Before the list loads, assume yes so nothing flashes out of the nav.
    has: (feature) => entitlements === null ? true : entitlements.includes(feature),
    known,
    // Billing B4: show "Add to your plan" only when the shop's list is known and
    // the feature is not on it. Never true while loading or after a failed load.
    lacks: (feature) => known && Array.isArray(entitlements) && !entitlements.includes(feature),
    onboarding,
    loaded,
    reload,
  }), [entitlements, known, onboarding, loaded, reload]);

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
};

export const useEntitlements = () => useContext(EntitlementsContext);
export const useEntitlement = (feature) => useContext(EntitlementsContext).has(feature);
export const useLacksEntitlement = (feature) => useContext(EntitlementsContext).lacks(feature);

export default EntitlementsContext;
